import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { MessageSquareReply, Check, RefreshCw, Send, Reply, X, Inbox } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { useSmsInboundSocket } from '../../hooks/useSmsInboundSocket';
import { playReplyChime } from '../../lib/replyChime';
import { TemplateChips } from './TemplateChips';
import { QuickReplyOutcomes } from './QuickReplyOutcomes';
import { SmsThreadDialog } from '../custom/SmsThreadDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MANAGER_ROLES = ['master_admin', 'domain_admin', 'manager', 'admin', 'domain_supervisor', 'supervisor', 'office_manager'];
const SLA_MINUTES = 15;

const ago = (iso) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.round(m / 60)}h ago`;
  return `${Math.round(m / 1440)}d ago`;
};

const SlaTimer = ({ receivedAt }) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const elapsed = Math.max(0, Date.now() - new Date(receivedAt).getTime());
  const remainMs = SLA_MINUTES * 60000 - elapsed;
  if (remainMs > 0) {
    const mm = Math.floor(remainMs / 60000);
    const ss = Math.floor((remainMs % 60000) / 1000);
    const warm = remainMs < 5 * 60000;
    return (
      <span
        className={`text-[11px] font-bold font-mono px-2 py-0.5 rounded-full ${warm ? 'bg-orange-100 text-orange-700 animate-pulse' : 'bg-emerald-100 text-emerald-700'}`}
        title={`Reply within ${SLA_MINUTES} min while the lead is hot`}
        data-testid="reply-sla-timer"
      >
        {mm}:{String(ss).padStart(2, '0')} to reply
      </span>
    );
  }
  const overMin = Math.floor(-remainMs / 60000);
  const overLabel = overMin < 60 ? `${overMin}m` : overMin < 1440 ? `${Math.round(overMin / 60)}h` : `${Math.round(overMin / 1440)}d`;
  return (
    <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700" title="Past the 15-minute hot window" data-testid="reply-sla-overdue">
      Going cold · {overLabel} over
    </span>
  );
};

export const RepliesInboxCard = () => {
  const { user } = useAuth();
  const isManager = MANAGER_ROLES.includes(user?.role);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [draft, setDraft] = useState('');
  const [threadPhone, setThreadPhone] = useState(null);
  const [pickedTemplate, setPickedTemplate] = useState(null);
  const [sending, setSending] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(() => {
    axios.get(`${API}/sms/replies`, { params: { days: 14 } })
      .then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  useSmsInboundSocket(useCallback((inb) => {
    playReplyChime();
    toast.info(`New reply from ${inb.from_phone}: “${(inb.content || '').slice(0, 60)}”`);
    load();
  }, [load]));

  const markHandled = async (id) => {
    setBusy(id);
    try {
      await axios.post(`${API}/sms/replies/${id}/handled`, { handled: true });
      toast.success('Marked handled');
      load();
    } catch (e) { toast.error('Failed to update'); }
    setBusy(null);
  };

  const sendReply = async (id) => {
    if (!draft.trim()) return;
    setSending(true);
    try {
      const templateId = pickedTemplate && draft.trim() === pickedTemplate.content ? pickedTemplate.id : null;
      await axios.post(`${API}/sms/replies/${id}/quick-reply`, { content: draft.trim(), template_id: templateId });
      toast.success('Reply sent');
      setReplyingTo(null);
      setDraft('');
      setPickedTemplate(null);
      setRefreshKey((k) => k + 1);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    }
    setSending(false);
  };

  if (!data) return null;
  const pending = (data.replies || []).filter((r) => !r.answered);
  const noInboundAtAll = (data.replies || []).length === 0;

  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 mb-4" data-testid="replies-inbox-card">
      <SmsThreadDialog phone={threadPhone} onClose={() => setThreadPhone(null)} />
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <p className="font-semibold text-slate-900 flex items-center gap-2">
          <MessageSquareReply className="w-5 h-5 text-amber-600" />
          {pending.length > 0 ? 'Prospect replies waiting' : 'Reply Inbox'}
          {pending.length > 0 && (
            <span className="text-xs font-bold bg-amber-500 text-white rounded-full px-2 py-0.5" data-testid="replies-unanswered-count">{pending.length}</span>
          )}
        </p>
        <button onClick={load} className="text-xs text-amber-700 hover:text-amber-900 inline-flex items-center gap-1" data-testid="replies-refresh">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>
      {pending.length === 0 ? (
        <div className="flex items-center gap-2.5 bg-white border border-amber-200 rounded-lg px-3 py-2.5" data-testid="replies-empty-state">
          <Inbox className="w-4 h-4 text-emerald-600 shrink-0" />
          <div>
            <p className="text-sm text-slate-700 font-medium">All caught up — no unanswered prospect replies in the last 14 days.</p>
            {noInboundAtAll && isManager && (
              <p className="text-[11px] text-slate-400 mt-0.5" data-testid="replies-webhook-hint">
                No inbound SMS received yet. If prospects are texting back but nothing lands here, check the Yabbr webhook registration (Admin → Yabbit SMS) and your Yabbr API key.
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {pending.slice(0, 8).map((r) => (
            <div key={r.id} className="bg-white border border-amber-200 rounded-lg px-3 py-2" data-testid={`reply-row-${r.id}`}>
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <p className="text-sm text-slate-900 flex items-center gap-2 flex-wrap">
                    <button onClick={() => setThreadPhone(r.from_phone)}
                      className="font-semibold hover:text-teal-600 hover:underline underline-offset-2"
                      title="View full conversation"
                      data-testid={`reply-thread-${r.id}`}>{r.from_phone}</button>
                    <span className="text-xs text-slate-400">· {ago(r.received_at)}</span>
                    <SlaTimer receivedAt={r.received_at} />
                  </p>
                  <p className="text-sm text-slate-700 mt-0.5">“{r.content}”</p>
                  {r.original?.content && <p className="text-[11px] text-slate-400 mt-0.5">Your SMS: “{r.original.content.slice(0, 120)}”</p>}
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => { setReplyingTo(replyingTo === r.id ? null : r.id); setDraft(''); setPickedTemplate(null); }}
                    className="text-xs font-semibold px-2.5 py-1.5 rounded-md bg-amber-500 hover:bg-amber-400 text-white inline-flex items-center gap-1"
                    data-testid={`reply-quick-${r.id}`}>
                    {replyingTo === r.id ? <X className="w-3.5 h-3.5" /> : <Reply className="w-3.5 h-3.5" />} {replyingTo === r.id ? 'Cancel' : 'Reply'}
                  </button>
                  <button onClick={() => markHandled(r.id)} disabled={busy === r.id}
                    className="text-xs font-semibold px-2.5 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white inline-flex items-center gap-1"
                    data-testid={`reply-handled-${r.id}`}>
                    <Check className="w-3.5 h-3.5" /> Handled
                  </button>
                </div>
              </div>
              {replyingTo === r.id && (
                <div className="mt-2" data-testid={`reply-composer-${r.id}`}>
                  <TemplateChips domainId={r.domain_id} onPick={(t) => { setDraft(t.content); setPickedTemplate(t); }} isManager={isManager} />
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value.slice(0, 459))}
                        rows={2}
                        autoFocus
                        placeholder={`Text ${r.from_phone} back…`}
                        className="w-full text-sm text-slate-900 bg-white placeholder:text-slate-400 border border-amber-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                        data-testid={`reply-composer-input-${r.id}`}
                      />
                      <p className="text-[10px] text-slate-400 text-right">{draft.length}/459</p>
                    </div>
                    <button onClick={() => sendReply(r.id)} disabled={sending || !draft.trim()}
                      className="text-xs font-semibold px-3 py-2 mb-4 rounded-md bg-teal-600 hover:bg-teal-500 disabled:opacity-50 text-white inline-flex items-center gap-1"
                      data-testid={`reply-send-${r.id}`}>
                      <Send className="w-3.5 h-3.5" /> {sending ? 'Sending…' : 'Send SMS'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {pending.length > 0 && (
        <p className="text-[11px] text-amber-700/70 mt-2">Sending them a new SMS also clears the reply automatically. Replies within {SLA_MINUTES} minutes convert best.</p>
      )}
      <QuickReplyOutcomes refreshKey={refreshKey} />
    </div>
  );
};
